import { QueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import defaultChatAvatarUrl from '../assets/chat_avatar.png';
import defaultHeaderAvatarUrl from '../assets/your_company.png';
import { SnackbarContextType } from '../contexes/SnackBarProvider';
import { ChatAgent, ChatAgentFormData } from '../interfaces/ChatAgent';
import { ResponseObject } from '../interfaces/App';
import { KnowledgeFileData } from '../interfaces/Knowledge';
import Knowledge from '../interfaces/Knowledge';
import User from '../interfaces/User';
import {
  createChatAgent,
  updateChatAgent,
  uploadAvatar,
} from '../services/chatAgentService';
import { TranslationKey } from '../types/i18n';
import { resolveCreateAvatarData } from './chatAgentAvatarUpload';
import {
  createNewKnowledge,
  getOrCreateKnowledgeId,
  saveWebscrapConfig,
  uploadKnowledgeFiles,
} from './knowledgeHelper';

export interface CreateChatAgentSubmitContext {
  methods: UseFormReturn<ChatAgentFormData>;
  currentUser: User | null;
  knowledgeFilesData: KnowledgeFileData[];
  executeStagedDeletions: () => Promise<{ failed: string[] }>;
  queryClient: QueryClient;
  displayMessage: SnackbarContextType;
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  setIsKnowledgeCreated: (value: boolean) => void;
  setIsAgentCreated: (value: boolean) => void;
  setIsFilesUploaded: (value: boolean) => void;
  setNewAgent: (agent: ChatAgent) => void;
}

// Handles the CREATE-workflow branch of ChatAgentForm's submit: creates the
// knowledge base (or reuses an existing one), creates the chat agent
// (without avatar URLs, since no agent id exists yet), uploads the avatar
// images and knowledge files, and flushes staged deletions. Throws on
// failure — the caller's shared try/catch handles displaying the error and
// resetting isProcessing. See docs/specs/agent-form-click-to-jump-navigation.md.
export const submitCreateChatAgent = async (
  formData: ChatAgentFormData,
  ctx: CreateChatAgentSubmitContext
): Promise<void> => {
  const {
    methods,
    currentUser,
    knowledgeFilesData,
    executeStagedDeletions,
    queryClient,
    displayMessage,
    t,
    setIsKnowledgeCreated,
    setIsAgentCreated,
    setIsFilesUploaded,
    setNewAgent,
  } = ctx;
  const { dirtyFields } = methods.formState;

  //
  // Reuse an existing knowledge base, create a new one, or — when the user
  // opted out — go without (the chat runtime skips RAG for an empty
  // knowledge_ids).
  //
  let knowledgeId: string | undefined;
  if (formData.knowledge_mode !== 'none') {
    try {
      knowledgeId = await getOrCreateKnowledgeId(
        formData.existing_knowledge_id,
        () =>
          createNewKnowledge(
            formData.knowledge_name,
            currentUser!.id,
            currentUser!.company_id,
            formData.knowledge_language
          )
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'Knowledge creation failed'
      ) {
        throw new Error(t('Knowledge creation failed.'));
      }
      throw error;
    }
    formData.knowledge_ids?.push(knowledgeId);
  }
  setIsKnowledgeCreated(true);

  // The create endpoint doesn't accept webscrap config directly (it
  // needs a knowledge id to attach to), so persist it with a
  // follow-up update once the knowledge exists. Non-fatal: the warning is
  // reported with the rest once the agent is created. Skipped if this is a
  // reused existing knowledge base, or if the user opted out of having one.
  let webscrapWarning: string | undefined;
  if (
    knowledgeId &&
    !formData.existing_knowledge_id &&
    formData.webscrap_enabled
  ) {
    webscrapWarning = await saveWebscrapConfig(knowledgeId, formData, t);
  }

  //
  // Store avatar data before clearing from formData
  //
  const headerAvatarData = await resolveCreateAvatarData({
    isDirty: !!dirtyFields.header_avatar_url,
    dataUrl: formData.header_avatar_url,
    fileName: formData.header_avatar_url_filename,
    defaultAssetUrl: defaultHeaderAvatarUrl,
    defaultAssetFileName: 'your_company.png',
  });

  const chatAvatarData = await resolveCreateAvatarData({
    isDirty: !!dirtyFields.chat_avatar_url,
    dataUrl: formData.chat_avatar_url,
    fileName: formData.chat_avatar_url_filename,
    defaultAssetUrl: defaultChatAvatarUrl,
    defaultAssetFileName: 'chat_avatar.png',
  });

  // Clear avatar URLs before creating agent (will be set after upload)
  formData.header_avatar_url = '';
  formData.chat_avatar_url = '';

  //
  // Create a new chat agent first (without avatar URLs)
  //
  const response: ResponseObject = await createChatAgent(formData);
  if (response?.status !== 'success') {
    throw new Error(t('Chat agent creation failed'));
  }
  setIsAgentCreated(true);
  const newChatAgent = response.message as ChatAgent;
  setNewAgent(newChatAgent);

  //
  // Upload the avatar images with the new agent ID
  //
  const avatarUpdates: Partial<ChatAgentFormData> = {};

  const newHeaderAvatar = await uploadAvatar(
    headerAvatarData.fileName,
    headerAvatarData.base64Content,
    currentUser!.id,
    newChatAgent.id
  );
  avatarUpdates.header_avatar_url = newHeaderAvatar?.message?.file_url;

  const newChatAvatar = await uploadAvatar(
    chatAvatarData.fileName,
    chatAvatarData.base64Content,
    currentUser!.id,
    newChatAgent.id
  );
  avatarUpdates.chat_avatar_url = newChatAvatar?.message?.file_url;

  // Update the chat agent with avatar URLs if any were uploaded
  if (Object.keys(avatarUpdates).length > 0) {
    await updateChatAgent(newChatAgent.id, avatarUpdates);
  }

  //
  // Upload the knowledge files and flush any staged deletions in
  // parallel, mirroring AgentForm/KnowledgeForm's submit wiring.
  // Settled (not `all`) so a failed upload can't suppress the
  // report of deletions that already completed.
  //
  const [createUploadResult, createDeletionResult] = await Promise.allSettled([
    knowledgeId
      ? uploadKnowledgeFiles(knowledgeFilesData, {
          id: knowledgeId,
        } as Knowledge)
      : Promise.resolve({ failed: [] as string[] }),
    executeStagedDeletions(),
  ]);
  setIsFilesUploaded(true);

  // Each part is a complete, independently-translated sentence, joined
  // with a space so translated output stays grammatically valid in every
  // locale.
  const createParts: string[] = webscrapWarning ? [webscrapWarning] : [];
  if (
    createUploadResult.status === 'fulfilled' &&
    createUploadResult.value.failed.length > 0
  ) {
    createParts.push(
      t('Could not upload: {{files}}', {
        files: createUploadResult.value.failed.join(', '),
      })
    );
  }
  if (createDeletionResult.status === 'fulfilled') {
    if (createDeletionResult.value.failed.length > 0) {
      createParts.push(
        t('Could not delete: {{files}}', {
          files: createDeletionResult.value.failed.join(', '),
        })
      );
    }
  } else {
    createParts.push(
      createDeletionResult.reason instanceof Error
        ? createDeletionResult.reason.message
        : t('Some staged file deletions could not be completed.')
    );
  }

  if (createParts.length > 0) {
    displayMessage(
      [t('Chat agent created successfully.'), ...createParts].join(' '),
      'warning',
      6000
    );
  } else {
    displayMessage(t('Chat agent created successfully.'), 'success');
  }

  // Invalidate caches so the knowledge table reflects the new attachment
  queryClient.invalidateQueries({ queryKey: ['chat-agents'] });
  queryClient.invalidateQueries({ queryKey: ['knowledges'] });
  queryClient.invalidateQueries({
    queryKey: ['knowledges-with-attachment'],
  });
};
